import React, { useState } from 'react';

import { translations } from '../../locale/translations';
import { RE_EMAIL } from '../../utils/util';
import { SignUpProps } from '../Credentials';
import {
    Button,
    CheckboxWrapper,
    ErrorMessage,
    FormControl,
    FormErrorMessage,
    GoogleButton,
    GoogleLogo,
    Input,
    Lines,
    Link,
    Separator,
    SwitchPage,
    Text,
    Title
} from '../../styles/shared.styles';
import { PasswordHint } from '../../components/PasswordHint';

export const CreateAccount = ({
    lang,
    formState,
    setFormState,
    switchPage,
    signupType,
    mixPanelTracker,
    googleLogin,
    googleTracker,
    afterLogin,
    setUserId,
    hubspotTracker,
    signup
}: SignUpProps) => {
    const [showErrors, setShowErrors] = useState(false);
    const [currentError, setCurrentError] = useState<string | null>(null);
    const [showHint, setShowHint] = useState(false);
    const locale = translations[lang];
    const p = formState.password || '';
    const hint: Array<[boolean, string]> = [
        [p.length >= 8, locale.passHintLen],
        [/[A-Z]{1,}/g.test(p) && /[a-z]{1,}/.test(p), locale.passHintCase],
        [/[^\w\s]/.test(p), locale.passHintChar],
        [/\d/.test(p), locale.passHintNum]
    ];

    const emailValid = formState.email.length && RE_EMAIL.test(formState.email);
    const passwordValid = !hint.some(([valid]) => !valid);
    const isValid = emailValid && passwordValid;

    const handleEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormState({ email: e.target.value });
        setShowErrors(false);
    };
    const handlePassword = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormState({ password: e.target.value });
        setShowErrors(false);
    };
    const handleMarketing = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormState({ marketing: e.target.checked });
        setShowErrors(false);
    };

    async function onSignup() {
        setShowErrors(true);
        if (!isValid) return;

        const userType = formState.isBusiness ? 'business' : 'private';

        try {
            await mixPanelTracker.track('SignUp', 'CreateAccount', {
                userType: userType,
                marketing: !!formState.marketing,
                passwordFail: false,
                emailFail: false,
                signupType: signupType
            });

            const { userId } = await signup({
                email: formState.email,
                password: formState.password,
                marketing: formState.marketing,
                isBusiness: formState.isBusiness
            });

            await hubspotTracker.sendTrialSignupFormDataToHubspot({
                email: formState.email,
                isBusiness: formState.isBusiness,
                marketing: formState.marketing,
                user_locale: lang
            });

            setUserId(userId);

            await googleTracker.trackSignup(userId);
            await mixPanelTracker.alias(userId);
            await mixPanelTracker.track('SignUp', 'Success', { signupType: signupType, userType });

            if (formState.isBusiness) {
                switchPage('BusinessSignup');
            } else {
                afterLogin({ justSignedUp: true });
            }
        } catch (error) {
            await mixPanelTracker.track('SignUp', 'Error', { signupType: signupType, userType });

            return setCurrentError((error as any).message);
        }
    }

    async function signUpWithGoogle() {
        const userType = formState.isBusiness ? 'business' : 'private';

        try {
            await mixPanelTracker.track('SignUpGoogle', 'CreateAccount', {
                userType,
                marketing: !!formState.marketing,
                passwordFail: false,
                emailFail: false,
                signupType: signupType
            });

            const { userId, justSignedUp, email, displayName } = await googleLogin(formState.isBusiness);

            setUserId(userId);
            setFormState({ ...formState, email, displayName });

            await hubspotTracker.sendTrialSignupFormDataToHubspot(
                {
                    ...formState,
                    email,
                    displayName,
                    user_locale: lang
                },
                'Ninox Trial Signup (with Google)'
            );

            await mixPanelTracker.track('SignUpGoogle', justSignedUp ? 'SignedUp' : 'LoggedIn', {
                userType,
                justSignedUp,
                signupType: signupType
            });

            if (!justSignedUp) {
                await googleTracker.trackLogin(userId);
                await mixPanelTracker.identify(userId);
            } else {
                await googleTracker.trackSignup(userId);
                await mixPanelTracker.alias(userId);
            }

            if (!justSignedUp || !formState.isBusiness) {
                afterLogin({ justSignedUp, loginSuccessful: true });
            } else {
                switchPage('BusinessSignup');
            }
        } catch (error) {
            await mixPanelTracker.track('SignUpGoogle', 'Error', { signupType: signupType, userType });

            return setCurrentError((error as any).message);
        }
    }

    return (
        <>
            <Title>{locale.signUpHeader2}</Title>
            <FormControl required>
                <label>{locale.emailAddress}</label>
                <Input
                    error={showErrors && !emailValid}
                    required
                    type="email"
                    value={formState.email}
                    onChange={handleEmail}
                />
                {showErrors && !emailValid && <ErrorMessage>{locale.invalidUserEMail}</ErrorMessage>}
            </FormControl>
            <FormControl required>
                <label>{locale.password}</label>
                <Input
                    autoComplete="off"
                    error={showErrors && !passwordValid}
                    type="password"
                    value={formState.password}
                    onBlur={() => setShowHint(false)}
                    onChange={handlePassword}
                    onFocus={() => setShowHint(true)}
                />
                {showErrors && !passwordValid && <ErrorMessage>{locale.wrongPassword}</ErrorMessage>}

                {showHint && <PasswordHint hints={hint} lang={lang} />}
            </FormControl>

            <FormControl>
                <CheckboxWrapper>
                    <input checked={formState.marketing} name="marketing" type="checkbox" onChange={handleMarketing} />
                    <div>{locale.signUpNewsletter}</div>
                </CheckboxWrapper>
            </FormControl>

            {showErrors && currentError && <FormErrorMessage>{currentError}</FormErrorMessage>}

            <Button data-testid="create-account" onClick={onSignup}>
                {locale.register}
            </Button>

            <div>
                {locale.termsDisclamer.split('$')[0]}
                <Link href="https://ninox.com/terms" style={{ display: 'unset' }} target="_blank">
                    {locale.termsDisclamer.split('$')[1]}
                </Link>
                {locale.termsDisclamer.split('$')[2]}
                <Link href="https://ninox.com/privacy" style={{ display: 'unset' }} target="_blank">
                    {locale.termsDisclamer.split('$')[3]}
                </Link>
                {locale.termsDisclamer.split('$')[4]}
            </div>

            <Separator>
                <Lines>
                    <Text>{locale.op_or}</Text>
                </Lines>
            </Separator>

            <GoogleButton onClick={signUpWithGoogle}>
                <GoogleLogo />
                <span>{locale.loginWithGoogle}</span>
            </GoogleButton>

            <SwitchPage>
                {locale.haveAnAccount}{' '}
                <Link data-testid="login" onClick={() => switchPage('SignIn')}>
                    {locale.login2}
                </Link>
            </SwitchPage>
        </>
    );
};
